import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Notification } from "@/lib/types/dashboard";

interface AppUIState {
  // Sidebar state
  leftSidebarOpen: boolean;
  rightSidebarOpen: boolean;
  mobileAiSheetOpen: boolean;

  // Notifications
  notifications: Notification[];

  // Actions
  setLeftSidebarOpen: (open: boolean) => void;
  toggleLeftSidebar: () => void;
  setRightSidebarOpen: (open: boolean) => void;
  toggleRightSidebar: () => void;
  setMobileAiSheetOpen: (open: boolean) => void;
  toggleMobileAiSheet: () => void;
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markNotificationAsRead: (id: string) => void;
  markAllNotificationsAsRead: () => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Computed getters
  getUnreadNotificationCount: () => number;
}

export const useAppUIStore = create<AppUIState>()(
  persist(
    (set, get) => ({
      // Initial state
      leftSidebarOpen: false,
      rightSidebarOpen: false,
      mobileAiSheetOpen: false,
      notifications: [],

      // Sidebar actions
      setLeftSidebarOpen: (open: boolean) => set({ leftSidebarOpen: open }),

      toggleLeftSidebar: () => set((state) => ({ leftSidebarOpen: !state.leftSidebarOpen })),

      setRightSidebarOpen: (open: boolean) => set({ rightSidebarOpen: open }),

      toggleRightSidebar: () => set((state) => ({ rightSidebarOpen: !state.rightSidebarOpen })),

      setMobileAiSheetOpen: (open: boolean) => set({ mobileAiSheetOpen: open }),

      toggleMobileAiSheet: () => set((state) => ({ mobileAiSheetOpen: !state.mobileAiSheetOpen })),

      // Notification actions
      setNotifications: (notifications: Notification[]) => set({ notifications }),

      addNotification: (notification: Notification) =>
        set((state) => ({
          notifications: [notification, ...state.notifications],
        })),

      markNotificationAsRead: (id: string) =>
        set((state) => ({
          notifications: state.notifications.map((notification) =>
            notification.id === id ? { ...notification, isRead: true } : notification
          ),
        })),

      markAllNotificationsAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map((notification) => ({
            ...notification,
            isRead: true,
          })),
        })),

      removeNotification: (id: string) =>
        set((state) => ({
          notifications: state.notifications.filter((notification) => notification.id !== id),
        })),

      clearNotifications: () => set({ notifications: [] }),

      // Computed getter
      getUnreadNotificationCount: () => {
        const state = get();
        return state.notifications.filter((n) => !n.isRead).length;
      },
    }),
    {
      name: "app-ui-store",
      // Only persist sidebar state, not notifications (they should be fresh on reload)
      partialize: (state) => ({
        leftSidebarOpen: state.leftSidebarOpen,
        rightSidebarOpen: state.rightSidebarOpen,
        mobileAiSheetOpen: state.mobileAiSheetOpen,
      }),
    }
  )
);
